import React, { useState } from 'react'
import t from '../service/translate.service'
import MaskedInput from 'react-text-mask'
import { Form } from 'react-bootstrap'
import Select from 'react-select'
import UploadPreview from './UploadPreview'
import PropTypes from 'prop-types'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import CalendarIcon from './icons/calendar'
import { useTheme } from '../providers/ThemeProvider'

export const Input = React.forwardRef(({
  label,
  name = '',
  value,
  onChange,
  validate,
  type = 'text',
  placeholder = t.FORM_PLACEHOLDER(label),
  helper,
  disabled,
  isLabel = true,
  styles,
  autoComplete = true,
  required = false,
  blurValidate
}, ref) => {
  const [isBlur, setIsBlur] = useState(false)
  return (
    <Form.Group style={styles} className='form-group'>
      { isLabel && <Form.Label>{`${label} ${required ? '*' : ''}`}</Form.Label> }
      {disabled ? <Form.Control
        disabled
        type={type}
        value={value}
        onChange={onChange}
        name={name}
        placeholder={placeholder}
        aria-label={label} />
        : (
          <Form.Control
            ref={ref}
            type={type}
            value={value}
            onChange={onChange}
            onBlur={() => setIsBlur(true)}
            name={name}
            placeholder={placeholder}
            autoComplete={autoComplete.toString()}
            aria-label={label}
          />
        )}

      {handleErrorMessage({ validate: blurValidate && isBlur ? blurValidate : validate, value, label })}
      {helper && <Form.Text className="text-muted">{helper}</Form.Text>}
    </Form.Group>
  );
})
Input.propTypes = {
  label: PropTypes.string,
  isLabel: PropTypes.bool,
  name: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  validate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
    PropTypes.func
  ]),
  type: PropTypes.string,
  placeholder: PropTypes.string,
  helper: PropTypes.object,
  disabled: PropTypes.bool,
  styles: PropTypes.object
}
export const Textarea = React.forwardRef(({
  label,
  value,
  placeholder = t.FORM_PLACEHOLDER(label),
  onChange,
  validate,
  type = 'text',
  name = '',
  maxLength = 100000,
  required = false,
  blurValidate
}, ref) => {
  const [isBlur, setIsBlur] = useState(false)
  return (
    <Form.Group className='form-group'>
      <div className='input-div'>
        <Form.Label>{`${label} ${required ? '*' : ''}`}</Form.Label>
        <textarea
          ref={ref}
          maxLength={maxLength}
          name={name}
          placeholder={placeholder}
          rows={6}
          className="TextInput"
          type={type}
          value={value}
          onChange={onChange}
          onBlur={() => {
            console.log(1)
            setIsBlur(true)
          }}
          aria-label={label}
        />
      {handleErrorMessage({ validate: blurValidate && isBlur ? blurValidate : validate, value, label })}
      </div>
    </Form.Group>
  );
})

Textarea.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  validate: PropTypes.func,
  placeholder: PropTypes.string,
  type: PropTypes.string,
  name: PropTypes.string
}
export const Checkbox = React.forwardRef(
  (
    {
      label,
      helperLabel,
      value,
      onChange,
      validate,
      error,
      labelBeforeText,
      labelLink,
      labelLinkText,
      labelAfterText,
      name,
      blurValidate,
      id = ''
    },
    ref
  ) => {
    const [isBlur, setIsBlur] = useState(false)

    const handleCheckboxClick = (e) => {
      e.preventDefault()
      onChange({ target: { name, checked: !value } })
    }

    return (
      <Form.Group className="form-group">
        <div className="custom-checkmark-wrapper">
          <div className="custom-checkmark" style={{ alignItems: 'start' }}>
            <input
              type="checkbox"
              checked={value}
              onChange={() => {}}
              tabIndex={-1}
              name={name}
              style={{ display: 'none' }}
            />

            <button
              id={id}
              type="button"
              className="checkmark"
              role="checkbox"
              aria-checked={value}
              onClick={(e) => {
                e.preventDefault()
                onChange({ target: { name, checked: !value } })
              }}
              onBlur={() => setIsBlur(true)}
              ref={ref}
            />

            <div className="content" id="checkbox-label">
              {label ? (
                label
              ) : (
                <>
                  {labelBeforeText} <a href={labelLink}>{labelLinkText}</a>
                  {labelAfterText}
                </>
              )}
            </div>
          </div>
        </div>

        {handleErrorMessage({
          validate: blurValidate && isBlur ? blurValidate : validate,
          value,
          label: label || helperLabel || ''
        })}
      </Form.Group>
    )
  }
)

Checkbox.propTypes = {
  label: PropTypes.string,
  labelLink: PropTypes.string,
  labelLinkText: PropTypes.string,
  labelBeforeText: PropTypes.string,
  labelAfterText: PropTypes.string,
  value: PropTypes.bool,
  error: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  validate: PropTypes.func,
  name: PropTypes.string
}

export const Masked = ({
  label,
  value,
  onChange,
  validate,
  mask,
  placeholder = t.FORM_PLACEHOLDER(label)
}) => {
  return (
    <Form.Group>
      <Form.Label>{label}</Form.Label>
      <MaskedInput
        className="TextInput"
        keepCharPositions
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        mask={mask}
        aria-label={label}
      />
      <div className="error">{validate && validate(value, label)}</div>
    </Form.Group>
  );
}

Masked.propTypes = {
  label: PropTypes.string.isRequired,
  mask: PropTypes.array.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  validate: PropTypes.func,
  placeholder: PropTypes.string
}
export const CustomSelect = React.forwardRef(({
  label,
  value,
  onChange,
  onInputChange,
  validate,
  options,
  placeholder = t.FORM_SELECT_PLACEHOLDER(label),
  isSearchable = false,
  inputValue,
  styles,
  defaultValue,
  isDisabled,
  required = false,
  blurValidate
}, ref) => {
  const [isBlur, setIsBlur] = useState(false)
  const {magnifyText} = useTheme()
  const colourStyles = {
    control: (baseStyles, state) => ({
      ...baseStyles,
      borderColor: '#dfdfdf!important',
      borderRadius: '0ox',
      padding: '10px 10px',
    }),
    placeholder: (baseStyles, state) => ({
      ...baseStyles,
      color: '#a1a1a1',
      fontSize: `${magnifyText(16)}px`
    }),
    option: (baseStyles, { isFocused, isSelected }) => ({
      ...baseStyles,
      color: '#212529',
      backgroundColor: isFocused ? '#eee' : isSelected ? '#eee' : '#fff',
      ':hover': {
        backgroundColor: '#eee'
      },
      ':active': {
        backgroundColor: '#eee'
      }
    }),
    singleValue: (baseStyles, state) => ({
      ...baseStyles,
      color: '#212529',
      fontSize: `${magnifyText(16)}px`
    }),
  }

  return (
    <Form.Group className='form-group'>
      <Form.Label>{`${label} ${required ? '*' : ''}`}</Form.Label>
      <div style={styles}>
        <Select
          ref={ref}
          styles={colourStyles}
          defaultValue={defaultValue}
          value={value}
          options={options}
          placeholder={placeholder}
          onChange={onChange}
          onBlur={() => setIsBlur(true)}
          onInputChange={onInputChange}
          isSearchable={isSearchable}
          inputValue={inputValue}
          closeMenuOnSelect
          isDisabled={isDisabled}
          aria-label={label}
        />
      </div>
      {handleErrorMessage({ validate: blurValidate && isBlur ? blurValidate : validate, value, label })}
    </Form.Group>
  );
})
export const ImageUploader = props => {
  const { validate } = props
  return (
    <Form.Group>
      <UploadPreview {...props} />
      {handleErrorMessage({ validate })}
    </Form.Group>
  )
}
ImageUploader.propTypes = {
  validate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool
  ])
}
CustomSelect.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array
  ]),
  value: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
    PropTypes.number
  ]),
  isSearchable: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onInputChange: PropTypes.func,
  inputValue: PropTypes.string,
  validate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
    PropTypes.func
  ]),
  placeholder: PropTypes.string,
  styles: PropTypes.object,
  defaultValue: PropTypes.object,
  isDisabled: PropTypes.bool
}

export const DateInput = React.forwardRef(({
  label,
  value,
  onChange,
  validate,
  minDate,
  maxDate = null,
  name = '',
  isLabel = true,
  blurValidate
}, ref) => {
  const [isBlur, setIsBlur] = useState(false)
  return (
    <Form.Group className="form-group">
      {isLabel && <Form.Label>{label}</Form.Label>}
      <div className="date-input-wrap">
        <DatePicker
          ref={ref}
          dateFormat="dd-MM-yyyy"
          selected={value}
          minDate={minDate}
          maxDate={maxDate}
          onChange={onChange}
          showYearDropdown
          adjustDateOnChange
          name={name}
          yearDropdownItemNumber={4}
          aria-label={label}
          onBlur={() => setIsBlur(true)}
        />
        <div className="icon-wrap" aria-hidden="true">
          {CalendarIcon}
        </div>
      </div>
      {handleErrorMessage({ validate: blurValidate && isBlur ? blurValidate : validate, value, label })}
    </Form.Group>
  );
})
DateInput.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  onChange: PropTypes.func.isRequired,
  validate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
    PropTypes.func
  ]),
  minDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  name: PropTypes.string,
  isLabel: PropTypes.bool
}

export const DateMonthInput = React.forwardRef(({
  label,
  value,
  onChange,
  validate,
  minDate,
  name = '',
  isLabel = true,
  blurValidate
}, ref) => {
  const [isBlur, setIsBlur] = useState(false)
  return (
    <Form.Group className='form-group'>
      { isLabel && <Form.Label>{label}</Form.Label> }
      <div className='date-input-wrap'>
        <DatePicker
          ref={ref}
          selected={value}
          minDate={minDate}
          onChange={onChange}
          dateFormat="MMMM yyyy"
          showMonthYearPicker
          adjustDateOnChange
          onBlur={() => setIsBlur(true)}
          name={name}
          yearDropdownItemNumber={4}
        />
        <div className='icon-wrap'>
          { CalendarIcon }
        </div>
      </div>
      {handleErrorMessage({ validate: blurValidate && isBlur ? blurValidate : validate, value, label })}
    </Form.Group>
  )
})
DateMonthInput.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  onChange: PropTypes.func.isRequired,
  validate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
    PropTypes.func
  ]),
  minDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  name: PropTypes.string,
  isLabel: PropTypes.bool
}

function handleErrorMessage ({ validate, value, label }) {
  return (
    validate && (
      <div  className='error-message'>
        {typeof validate === 'string' ? validate : validate(value, label)}
      </div>
    )
  )
}
function handleCheckboxErrorMessage ({ validate, value, label }) {
  return (
    validate && (
      <div className='error-message'>
        {typeof validate === 'string' ? validate : validate(value, label)}
      </div>
    )
  )
}
